<template>
    <v-lazy :v-model="image" :options="{ threshold: 0.5 }" class="fill-height">
        <v-hover v-slot="{ hover }">
            <v-card
                @click="show()"
                :elevation="hover ? 5 : 0"
                :class="{ 'on-hover': hover }"
                height="350"
                class="d-flex flex-column image-card"
            >
                <!-- Imagen -->
                <div class="image-container">
                    <v-img
                        :src="image320x320URL"
                        aspect-ratio="1"
                        class="grey lighten-2"
                        height="200"
                    >
                        <template v-slot:placeholder>
                            <v-row
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                            >
                                <v-progress-circular
                                    indeterminate
                                    color="grey lighten-5"
                                />
                            </v-row>
                        </template>
                    </v-img>
                </div>

                <!-- Título + menú -->
                <v-card-title
                    class="caption text-capitalize mt-n2 py-2 title-container"
                >
                    <h4 class="project-title">
                        {{ imageToUpdate.projectName || 'empty' }}
                    </h4>
                    <v-spacer></v-spacer>
                    <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon v-bind="attrs" v-on="on">
                                <v-icon>mdi-dots-horizontal</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item @click.stop="editTags(image)">
                                <v-dialog
                                    v-model="editDialog"
                                    persistent
                                    max-width="400px"
                                    :retain-focus="false"
                                >
                                    <v-card :loading="loading">
                                        <v-card-title class="text-h5"
                                            >Edit Tags</v-card-title
                                        >
                                        <v-card-text>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field
                                                        v-model="
                                                            imageToUpdate.projectName
                                                        "
                                                        label="Project Name"
                                                        hide-details
                                                        underlined
                                                        prepend-icon="mdi-form-textbox"
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <div
                                                        class="d-flex tag-input align-center flex-wrap"
                                                    >
                                                        <v-combobox
                                                            v-model="
                                                                imageToUpdate.tags
                                                            "
                                                            :items="tagsToEdit"
                                                            multiple
                                                            chips
                                                            deletable-chips
                                                            hide-details
                                                            label="Select Tags"
                                                        />
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                        <v-card-actions>
                                            <v-btn
                                                text
                                                color="secondary"
                                                @click="closeEditImage"
                                                >Close</v-btn
                                            >
                                            <v-spacer></v-spacer>
                                            <v-btn
                                                text
                                                color="primary"
                                                :loading="loading"
                                                @click="updateImage"
                                            >
                                                Update
                                            </v-btn>
                                        </v-card-actions>
                                    </v-card>
                                </v-dialog>
                                <v-list-item-title>Edit</v-list-item-title>
                            </v-list-item>

                            <v-list-item @click.stop="openDelete(image)">
                                <v-dialog
                                    v-model="deleteDialog"
                                    persistent
                                    max-width="400px"
                                    :retain-focus="false"
                                >
                                    <v-card :loading="loading">
                                        <v-card-title class="text-h5"
                                            >Delete Image</v-card-title
                                        >
                                        <v-card-text>
                                            Are you sure you want to delete this
                                            image?
                                        </v-card-text>
                                        <v-card-actions>
                                            <v-btn
                                                text
                                                color="secondary"
                                                @click="deleteDialog = false"
                                                >Close</v-btn
                                            >
                                            <v-spacer></v-spacer>
                                            <v-btn
                                                @click="deleteImage"
                                                text
                                                color="error"
                                                :loading="loading"
                                                >Delete</v-btn
                                            >
                                        </v-card-actions>
                                    </v-card>
                                </v-dialog>
                                <v-list-item-title>Delete</v-list-item-title>
                            </v-list-item>

                            <v-list-item @click="downloadFile(image)">
                                <v-list-item-title>Download</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-card-title>

                <!-- Tags -->
                <v-card-subtitle class="tags-subtitle-container">
                    <div class="tags-container">
                        <div class="tags-visible">
                            <v-chip
                                class="mr-1 mb-1"
                                x-small
                                flat
                                v-for="(tag, i) in imageToUpdate.tags"
                                :key="i"
                            >
                                {{ tag.toLowerCase() }}
                            </v-chip>
                        </div>
                    </div>
                </v-card-subtitle>
            </v-card>
        </v-hover>
    </v-lazy>
</template>

<script>
import { storage } from '@/services/firebase'
import _ from 'lodash'
import { mapMutations } from 'vuex'

export default {
    name: 'ToolImageCard',
    props: {
        uid: { type: String },
        totalImages: { type: Number },
        image: {
            type: Object,
            default() {
                return {
                    id: '',
                    tags: [],
                    file: '',
                    file320x320: null,
                    image320x320URL: null,
                }
            },
        },
        tags: { type: Array, default: () => [] },
    },
    data() {
        return {
            expandedCards: false,
            deleteDialog: false,
            editDialog: false,
            tag: '',
            loading: null,
            folderPictures: 'photo_galery',
            image320x320URL: null,
            imageToUpdate: null,
            tagsToEdit: [],
            companyId: JSON.parse(localStorage.getItem('company')),
        }
    },
    async created() {
        try {
            if (this.image.file320x320) {
                const path = `${this.companyId}/${this.folderPictures}`
                const storageRef = storage().ref(path)
                const picRef = storageRef.child(this.image.file320x320)
                await picRef.getDownloadURL().then(URL => {
                    this.image.image320x320URL = URL
                    this.image320x320URL = URL
                })
                const fileRef = storageRef.child(this.image.file)
                await fileRef.getDownloadURL().then(URL => {
                    this.image.imageURL = URL
                    this.$emit('loaded')
                })
            }
        } catch (error) {
            this.$emit('removeImage', this.image)
        }
    },
    mounted() {
        this.imageToUpdate = _.cloneDeep(this.image)
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        closeEditImage() {
            this.imageToUpdate = _.cloneDeep(this.image)
            this.editDialog = false
        },
        async downloadFile() {
            const path = `${this.companyId}/${this.folderPictures}`
            const storageRef = storage().ref(path)
            const picRef = storageRef.child(this.image.file)
            await picRef
                .getDownloadURL()
                .then(URL => {
                    this.image.imageURL = URL
                    window.open(this.image.imageURL, '_blank')
                })
                .catch(() => {})
        },
        openDelete() {
            this.deleteDialog = true
        },
        updateImage() {
            this.$emit('updateImage', this.uid, this.imageToUpdate)
            this.imageToUpdate = _.cloneDeep(this.imageToUpdate)
            this.editDialog = false
        },
        async editTags() {
            this.tagsToEdit = Array.from(
                new Set([
                    ...(this.tags || []),
                    ...(this.imageToUpdate.tags || []),
                ])
            )
            this.editDialog = true
        },
        deleteImage() {
            this.$emit('deleteImage', this.image)
        },
        show() {
            this.$emit('show', this.uid)
        },
    },
}
</script>

<style scoped>
.v-card.image-card {
    height: 350px !important;
    overflow: hidden;
}
.image-container {
    flex-shrink: 0;
    height: 200px;
}
.title-container {
    flex-shrink: 0;
    min-height: 56px;
    max-height: 56px;
}
.project-title {
    font-size: 0.9rem;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 40px);
}
.tags-subtitle-container {
    flex: 1;
    min-height: 0;
    padding: 8px 16px 12px 16px;
    overflow: hidden;
}
.tags-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.tags-visible {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;
}
.v-card.on-hover {
    transition: all 0.3s ease;
    transform: translateY(-2px);
}
</style>
